import { css } from "@emotion/css";
import type { InlineMessageType } from "@octopusdeploy/design-system-components";
import { Tooltip, InlineMessage, Switch } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { KubernetesTentacleEndpointResource, MachinePolicyResource, MachineResource, TentacleEndpointResource } from "@octopusdeploy/octopus-server-client";
import { EndpointsHelper, KubernetesAgentUpdateBehavior, UpgradeStatus } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { FormSection } from "~/components/form";
interface TentacleVersionInfo {
    tentacleVersion: string | null;
    upgradeStatus: UpgradeStatus;
    upgradeLocked: boolean;
    kubernetesDetails?: {
        agentVersion: string;
        helmReleaseName: string;
        namespace: string;
    };
}
function getTentacleVersionInfo(endpoint: TentacleEndpointResource | KubernetesTentacleEndpointResource): TentacleVersionInfo {
    if (EndpointsHelper.isTentacle(endpoint)) {
        const tentacleDetails = endpoint.TentacleVersionDetails;
        let upgradeStatus = UpgradeStatus.NoUpgrades;
        if (tentacleDetails.UpgradeRequired) {
            upgradeStatus = UpgradeStatus.UpgradeRequired;
        }
        else if (tentacleDetails.UpgradeSuggested) {
            upgradeStatus = UpgradeStatus.UpgradeSuggested;
        }
        else if (tentacleDetails.UpgradeAvailable) {
            upgradeStatus = UpgradeStatus.UpgradeAvailable;
        }
        return {
            tentacleVersion: tentacleDetails.Version,
            upgradeStatus,
            upgradeLocked: tentacleDetails.UpgradeLocked,
        };
    }
    if (EndpointsHelper.isKubernetesTentacle(endpoint)) {
        const kubernetesAgentDetails = endpoint.KubernetesAgentDetails;
        return {
            tentacleVersion: kubernetesAgentDetails?.TentacleVersion ?? null,
            upgradeStatus: kubernetesAgentDetails?.UpgradeStatus ?? UpgradeStatus.NoUpgrades,
            upgradeLocked: endpoint.UpgradeLocked,
            kubernetesDetails: {
                agentVersion: kubernetesAgentDetails?.AgentVersion ?? "",
                helmReleaseName: kubernetesAgentDetails?.HelmReleaseName ?? "",
                namespace: kubernetesAgentDetails?.KubernetesNamespace ?? "",
            },
        };
    }
    throw new Error(`Cannot get Version Details for Unsupported Endpoint ${endpoint}`);
}
function getTentacleUpgradeMessage(upgradeStatus: UpgradeStatus): {
    type: InlineMessageType;
    title: string;
} | null {
    switch (upgradeStatus) {
        case UpgradeStatus.NoUpgrades:
            return null;
        case UpgradeStatus.UpgradeAvailable:
            return { type: "information", title: "New version available" };
        case UpgradeStatus.UpgradeSuggested:
            return { type: "warning", title: "Upgrade suggested" };
        case UpgradeStatus.UpgradeRequired:
            return { type: "danger", title: "Upgrade required" };
    }
}
const styles = {
    lockDescription: css({
        marginBottom: space[8],
    }),
    versionContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[8],
    }),
};
interface TentacleDetailsProps {
    machine: MachineResource;
    machinePolicy: MachinePolicyResource;
    onToggleVersionLock?: (isLocked: boolean) => void;
    isTogglingLock?: boolean;
}
export function TentacleDetails({ machine, machinePolicy, onToggleVersionLock, isTogglingLock }: TentacleDetailsProps) {
    const { navigate } = useSpaceAwareNavigation();
    const { execute: upgradeTentacle, isExecuting: isUpgradingTentacle } = useMutation({
        name: "Upgrade Tentacle",
        action: async (repo) => {
            const res = await repo.Machines.upgradeTentacle(machine.Id);
            if (res.Tasks.length > 0) {
                return res.Tasks[0];
            }
            throw new Error("No upgrade task was created");
        },
        afterComplete: async (_, task) => navigate(links.taskPage.generateUrl({ taskId: task.Id })),
    });
    if (!EndpointsHelper.isAnyTentacle(machine.Endpoint)) {
        return null;
    }
    const { tentacleVersion, upgradeStatus, upgradeLocked, kubernetesDetails } = getTentacleVersionInfo(machine.Endpoint);
    if (!tentacleVersion) {
        return null;
    }
    let tentacleVersionInlineMessage: React.ReactNode = null;
    const hasTentacleUpgrade = upgradeStatus !== UpgradeStatus.NoUpgrades;
    const messageDetails = getTentacleUpgradeMessage(upgradeStatus);
    const policyBlocksUpgrades = EndpointsHelper.isKubernetesTentacle(machine.Endpoint) && machinePolicy.MachineUpdatePolicy.KubernetesAgentUpdateBehavior === KubernetesAgentUpdateBehavior.Block;
    if (hasTentacleUpgrade && policyBlocksUpgrades) {
        const action = {
            label: `View ${machinePolicy.Name}`,
            onClick: () => navigate(links.machinePolicyPage.generateUrl({
                spaceId: machine.SpaceId,
                machinePolicyId: machinePolicy.Id,
            })),
        };
        tentacleVersionInlineMessage = <InlineMessage type={"warning"} title={"Upgrade blocked by machine policy"} description={""} action={action}/>;
    }
    else if (hasTentacleUpgrade && upgradeLocked) {
        tentacleVersionInlineMessage = <InlineMessage type={"warning"} title={"Upgrade is locked to current version"} description={""}/>;
    }
    else if (messageDetails) {
        const action = {
            label: "Upgrade now",
            onClick: isUpgradingTentacle ? undefined : upgradeTentacle,
        };
        tentacleVersionInlineMessage = <InlineMessage type={messageDetails.type} title={messageDetails.title} description={""} action={action}/>;
    }
    const tentacleVersionDisplay = (<span className={styles.versionContainer}>
            {tentacleVersion}
            {tentacleVersionInlineMessage}
        </span>);
    const upgradeSwitch = <Switch value={upgradeLocked} onChange={onToggleVersionLock} disabled={isTogglingLock || policyBlocksUpgrades} label="Locked to current version"/>;
    return (<section>
            <FormSection title="Tentacle Version" help={tentacleVersionDisplay} includeBorder={true}/>
            {onToggleVersionLock && (<FormSection title="Lock Tentacle Version" help={<div>
                            <div className={styles.lockDescription}>Ensure this Tentacle doesn't automatically get updated past the current version.</div>
                            {policyBlocksUpgrades ? <Tooltip content={`Upgrade blocked by machine policy: ${machinePolicy.Name}`}>{upgradeSwitch}</Tooltip> : upgradeSwitch}
                        </div>} includeBorder={true}/>)}
            {kubernetesDetails && (<>
                    <FormSection title="Agent Version" help={kubernetesDetails.agentVersion} includeBorder={true}/>
                    <FormSection title="Helm Release Name" help={kubernetesDetails.helmReleaseName} includeBorder={true}/>
                    <FormSection title="Namespace" help={kubernetesDetails.namespace} includeBorder={true}/>
                </>)}
        </section>);
}
